<script lang="ts">
	import type { Device } from 'src/types/Device';
	import { format } from 'src/services/i18n';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { Button, SliderV2, Text, Toggle } from '@Shelly/shelly-design-system';
	import { getCameraConfig, saveCameraConfig } from '../../helpers/camera-settings-helpers';
	import type { CameraAudioState } from '../../types/camera-settings';

	export let device: Device;

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	const readAudio = (): CameraAudioState => {
		const audio = getCameraConfig(device)?.audio;
		return {
			inputEnable: audio?.input?.enable ?? true,
			volume: audio?.output?.volume ?? 100,
		};
	};

	let { inputEnable, volume } = readAudio();
	let cacheInputEnable = inputEnable;
	let cacheVolume = volume;
	let loading = false;

	$: hasChanges = inputEnable !== cacheInputEnable || volume !== cacheVolume;

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: save,
				loading,
				disabled: !hasChanges || loading,
				secondaryButton: {
					onClick: resetToDefaults,
					label: $format('button_reset_to_defaults', { default: 'Reset to defaults' }),
					variant: 'secondary',
				},
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	const persist = async (successMsg: string) => {
		loading = true;
		const success = await saveCameraConfig(
			device,
			{
				audio: {
					input: { enable: inputEnable },
					output: { volume },
				},
			},
			successMsg,
		);
		if (success) {
			cacheInputEnable = inputEnable;
			cacheVolume = volume;
		} else {
			inputEnable = cacheInputEnable;
			volume = cacheVolume;
		}
		loading = false;
	};

	const save = () => {
		if (!hasChanges) return;
		persist($format('setting_scc_saved_toast', { default: 'Setting saved successfully.' }));
	};

	const handleMicToggle = () => {
		inputEnable = !inputEnable;
		if ($showNewLayout) {
			persist(
				inputEnable
					? $format('camera_mic_enabled_toast', { default: 'Microphone enabled.' })
					: $format('camera_mic_disabled_toast', { default: 'Microphone disabled.' }),
			);
		}
	};

	const resetToDefaults = async () => {
		loading = true;
		const success = await saveCameraConfig(
			device,
			{ audio: null },
			$format('camera_audio_reset_success', { default: 'Audio settings reset to defaults.' }),
		);
		if (success) {
			({ inputEnable, volume } = readAudio());
			cacheInputEnable = inputEnable;
			cacheVolume = volume;
		}
		loading = false;
	};

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

<Text styleType="h2" class="mb-s">
	{$format('camera_audio_input', { default: 'Input' })}
</Text>

<Text styleType="tiny" class="text-white mb-s">
	{$format(`camera_audio_input_info`, {
		default: 'Enable or disable the microphone input.',
	})}
</Text>

<Toggle
	size="m"
	class="mb-m"
	checked={inputEnable}
	disabled={loading}
	on:click={handleMicToggle}
	id="camera-audio-input-enable"
>
	{$format('camera_audio_mic_enable', { default: 'Enable Microphone' })}
</Toggle>

<Text styleType="h2" class="mb-s">
	{$format('camera_audio_output', { default: 'Output' })}
</Text>

<SliderV2
	id="camera-audio-volume"
	class="mb-s"
	bind:value={volume}
	disabled={loading}
	min={0}
	max={100}
	step={1}
	unit="%"
	type="incremented"
	title={$format('camera_audio_speaker_volume', { default: 'Speaker volume' })}
	titleTypography="body-small"
	a11yProps={{ ariaLabel: 'Speaker volume', ariaLabelValue: 'Speaker volume value' }}
	lineMarkers={[
		{ value: 0, showLabel: true },
		{ value: 100, showLabel: true },
	]}
/>

{#if !$showNewLayout}
	<Button
		class="mb-s"
		variant="secondary"
		width="full"
		size="m"
		disabled={loading}
		on:click={resetToDefaults}
	>
		{$format('button_reset_to_defaults', { default: 'Reset to defaults' })}
	</Button>
	<Button width="full" size="m" {loading} disabled={!hasChanges || loading} on:click={save}>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}
